import { generateTaxBreakdownForFilings, useLoggedInUser } from '@commenda/api';
import {
  exportTaxBreakdowns,
  exportTaxBreakdownsWithLineItems,
} from '@commenda/api/new-sales-tax/filing';
import { getAllJobs } from '@commenda/api/new-sales-tax/job/list';
import { JobEntity, JobOperation } from '@commenda/api/new-sales-tax/job/types';
import {
  Badge,
  Button,
  CountryCode,
  Dropdown,
  FlagComponent,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { Country } from 'country-state-city';
import { BulkExportDeliveryType } from 'node_modules/@commenda/api/new-sales-tax/filing/types';
import { useNotification } from '../../../hooks/useNotification';
import {
  Filing,
  FilingFrequencyNameMap,
  FilingReviewStatus,
  FilingStatusBadge,
} from '../../../molecules';
import { JobsLogTable } from '../../../sales-tax/common/jobs';
import { getStateName } from '../../../sales-tax/utils';
import { ArchiveFilingModal } from '../modals/ArchiveFilingModal';
import { CompleteFilingModal } from '../modals/CompleteFilingModal';
import { PurchaseFilingModal } from '../modals/PurchaseFilingModal';
import { UnlockFilingModal } from '../modals/UnlockFilingModal';
import { COMMENDA_IO_EMAIL_DOMAIN } from '@commenda/utils';
import { canUnlockFiling } from '../../../sales-tax/filing/utils/filingApproval';

interface FilingPageHeaderProps {
  filing: Filing;
  onComplete: (filingId: string, file: FileBasic) => Promise<boolean>;
  onArchive: (filingId: string, reason: string) => Promise<boolean>;
  onUnlock?: (filingId: string) => Promise<boolean>;
  onRefresh: () => Promise<void>;
  showPurchaseOption?: boolean;
  showActions?: boolean;
  companyId: number;
  corporationId: number;
  onChatToggle?: () => void;
  isChatOpen?: boolean;
}

const FilingPageHeader = ({
  filing,
  onComplete,
  onArchive,
  onUnlock,
  onRefresh,
  showPurchaseOption = false,
  showActions = true,
  companyId,
  corporationId,
  onChatToggle,
  isChatOpen,
}: FilingPageHeaderProps) => {
  const { data: user } = useLoggedInUser();
  const isCommendaUser = user?.email?.toLowerCase()?.includes(COMMENDA_IO_EMAIL_DOMAIN);
  const completeFilingModalVisibility = useToggle();
  const archiveFilingModalVisibility = useToggle();
  const unlockFilingModalVisibility = useToggle();
  const purchaseFilingModalVisibility = useToggle();
  const exportJobTableVisibility = useToggle();
  const { setNotification } = useNotification();

  const { jobs: exportJobs } = getAllJobs({
    params: {
      corporation_id: corporationId,
      entity: JobEntity.FILING,
      operation: JobOperation.EXPORT,
      entity_id: filing.filing_id,
    },
  });

  // If no filing data, show placeholder or fallback UI
  if (!filing) {
    return (
      <div className="flex items-center justify-between gap-4xl">
        <div className="flex flex-col gap-md">
          <h2 className="display-xs-bold">Filing Information Unavailable</h2>
          <div className="flex items-center gap-md text-sm text-text-tertiary">
            <p>Filing data could not be loaded</p>
          </div>
        </div>
        <Button onClick={onRefresh} variant="secondary" size="sm">
          Refresh
        </Button>
      </div>
    );
  }

  const handleExportTaxBreakdowns = async (exportWithLineItems = false) => {
    try {
      let response;

      if (exportWithLineItems) {
        response = await exportTaxBreakdownsWithLineItems({
          filing_id: filing.filing_id,
          corporation_id: corporationId,
          delivery_type: BulkExportDeliveryType.EMAIL,
          delivery_recipients: [user.email],
        });
      } else {
        response = await exportTaxBreakdowns({
          filing_id: filing.filing_id,
          corporation_id: corporationId,
          delivery_type: BulkExportDeliveryType.EMAIL,
          delivery_recipients: [user.email],
        });
      }

      if (response.success) {
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message: 'Tax breakdown report export in progress',
        });
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: response.error || 'Failed to export Tax breakdown report',
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to export Tax breakdown report',
      });
    }
  };

  const handleGenerateTaxBreakdowmForFiling = async (filingId: string) => {
    try {
      // this will generate the numbers for all filings in PERIOD VISIBLE ON UI
      const response = await generateTaxBreakdownForFilings(corporationId, {
        filing_id: filingId,
      });

      if (response.success) {
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message: 'Tax breakdown generation in progress',
        });
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            response.error ||
            'Failed to start tax breakdown generation process',
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to start tax breakdown generation process',
      });
    }
  };

  const isCompletedOrFiled = filing.filingStatus === FilingReviewStatus.FILED;

  return (
    <div>
      <div className="flex items-center justify-between gap-4xl">
        <div className="flex flex-col gap-md">
          <h2 className="display-xs-bold">{filing.filingName}</h2>
          <div className="flex items-center gap-md text-sm text-text-tertiary">
            <FlagComponent
              _className="size-4"
              country={filing.corporation.incorporationCountry as CountryCode}
              countryName={
                Country.getCountryByCode(
                  filing.corporation.incorporationCountry
                )?.name
              }
            />
            <p>{filing.corporation.companyDisplayName}</p>
            <p>•</p>
            <p>
              {' '}
              {filing.jurisdiction ? getStateName(filing.jurisdiction) : '-'}
            </p>
            <p>•</p>
            <p>
              {FilingFrequencyNameMap[filing.frequency]} filing (
              {filing.filingPeriod})
            </p>
          </div>
        </div>

        <div className="flex items-center gap-xl">
          <div className="flex gap-lg border border-border-primary text-text-quaternary rounded-md px-md py-sm text-sm">
            <span className="text-text-quaternary txt-small-medium">
              Due:{' '}
              {new Date(filing.dueDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            <FilingStatusBadge
              status={filing.filingStatus as FilingReviewStatus}
            />
          </div>

          {onUnlock && canUnlockFiling(filing) && (
            <Button
              onClick={unlockFilingModalVisibility.show}
              variant="secondary"
              size="sm"
            >
              Unlock
            </Button>
          )}

          {onChatToggle && (
            <Button
              onClick={onChatToggle}
              variant={isChatOpen ? 'primary' : 'secondary'}
              size="sm"
            >
              <Icons.MessageCircle01 className="size-4" />
              Chat
            </Button>
          )}

          {showPurchaseOption && !isCompletedOrFiled && (
            <Button
              onClick={purchaseFilingModalVisibility.show}
              variant="secondary"
              size="sm"
            >
              Buy Filing
            </Button>
          )}

          {isCommendaUser && (
            <Dropdown
              align="end"
              itemClassName="py-md px-lg"
              contentClassName="w-76"
              menu={{
                groups: [
                  {
                    items: [
                      {
                        label: 'Generate breakdowns for filing',
                        icon: Icons.PlaySquare,
                        shortcut: (
                          <Badge variant="gray" size="sm">
                            Internal
                          </Badge>
                        ),
                        onClick: () =>
                          handleGenerateTaxBreakdowmForFiling(filing.filing_id),
                      },
                      {
                        icon: Icons.ReceiptCheck,
                        label: 'View generated tax breakdown reports',
                        shortcut: (
                          <Badge variant="gray" size="sm">
                            Internal
                          </Badge>
                        ),
                        onClick: exportJobTableVisibility.show,
                      },
                      {
                        label: 'Export tax breakdown report',
                        icon: Icons.FileDownload03,
                        shortcut: (
                          <Badge variant="gray" size="sm">
                            Internal
                          </Badge>
                        ),
                        onClick: () => handleExportTaxBreakdowns(),
                      },
                      {
                        label: 'Export tax breakdowns report with line items',
                        icon: Icons.FileDownload03,
                        shortcut: (
                          <Badge variant="gray" size="sm">
                            Internal
                          </Badge>
                        ),
                        onClick: () => handleExportTaxBreakdowns(true),
                      },

                      ...(showActions && !isCompletedOrFiled
                        ? [
                            {
                              label: 'Complete Filing',
                              onClick: completeFilingModalVisibility.show,
                              icon: Icons.Check,
                              shortcut: (
                                <Badge variant="gray" size="sm">
                                  Internal
                                </Badge>
                              ),
                            },
                            {
                              label: 'Archive filing',
                              icon: Icons.Archive,
                              onClick: archiveFilingModalVisibility.show,
                              labelClassName: 'text-utility-error-600',
                              iconClassName: 'text-utility-error-600',
                              shortcut: (
                                <Badge variant="gray" size="sm">
                                  Internal
                                </Badge>
                              ),
                            },
                          ]
                        : []),
                    ],
                  },
                ],
              }}
            >
              <div className="flex items-center p-2 hover:bg-bg-secondary rounded-md">
                <Icons.DotsVertical className="size-5 cursor-pointer" />
              </div>
            </Dropdown>
          )}
        </div>
      </div>

      <CompleteFilingModal
        isOpen={completeFilingModalVisibility.visible}
        onClose={completeFilingModalVisibility.toggle}
        filing={filing}
        onComplete={onComplete}
        onSuccess={onRefresh}
        companyId={companyId}
      />
      <ArchiveFilingModal
        isOpen={archiveFilingModalVisibility.visible}
        onClose={archiveFilingModalVisibility.toggle}
        filing={filing}
        onArchive={onArchive}
        onSuccess={onRefresh}
      />
      {onUnlock && (
        <UnlockFilingModal
          isOpen={unlockFilingModalVisibility.visible}
          onClose={unlockFilingModalVisibility.hide}
          filing={filing}
          onUnlock={onUnlock}
          onSuccess={onRefresh}
        />
      )}
      <PurchaseFilingModal
        isOpen={purchaseFilingModalVisibility.visible}
        onClose={purchaseFilingModalVisibility.toggle}
        filing={filing}
      />

      <JobsLogTable
        isOpen={exportJobTableVisibility.visible}
        onClose={exportJobTableVisibility.hide}
        jobOperation={JobOperation.EXPORT}
        data={exportJobs}
        corporationId={corporationId}
        title="Tax Breakdown Reports"
      />
    </div>
  );
};

export { FilingPageHeader };
